import { useEffect, useState } from "react";
import { Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, CloudDrizzle } from "lucide-react";

// Open-Meteo WMO weather codes → label + icon.
function describe(code) {
  if (code === 0) return { label: "Clear", Icon: Sun };
  if (code <= 3) return { label: "Cloudy", Icon: Cloud };
  if (code === 45 || code === 48) return { label: "Fog", Icon: CloudFog };
  if (code >= 51 && code <= 57) return { label: "Drizzle", Icon: CloudDrizzle };
  if (code >= 61 && code <= 67) return { label: "Rain", Icon: CloudRain };
  if (code >= 71 && code <= 77) return { label: "Snow", Icon: CloudSnow };
  if (code >= 80 && code <= 82) return { label: "Showers", Icon: CloudRain };
  if (code >= 95 && code <= 99) return { label: "Storm", Icon: CloudLightning };
  return { label: "Cloudy", Icon: Cloud };
}

const FALLBACK = { lat: 51.5074, lon: -0.1278, city: "London", country: "UK" };
const CACHE_KEY = "hinton.weather.v1";
const CACHE_MS = 30 * 60 * 1000; // 30 minutes

async function reverseGeocode(lat, lon) {
  try {
    const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`);
    if (!r.ok) return null;
    const d = await r.json();
    const city = d.city || d.locality || d.principalSubdivision || "";
    const country = d.countryCode || d.countryName || "";
    return city ? { city, country } : null;
  } catch { return null; }
}

async function fetchWeather(lat, lon) {
  const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&temperature_unit=celsius`);
  if (!r.ok) throw new Error("weather fetch failed");
  const d = await r.json();
  return {
    temp: Math.round(d.current?.temperature_2m),
    code: d.current?.weather_code ?? 0,
  };
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (Date.now() - v.at > CACHE_MS) return null;
    return v;
  } catch { return null; }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, at: Date.now() })); } catch { /* ignore */ }
}

export default function HeaderWeather() {
  const [state, setState] = useState(() => readCache());

  useEffect(() => {
    let cancelled = false;
    const cached = readCache();
    if (cached) setState(cached);

    const run = async (lat, lon, place) => {
      try {
        const w = await fetchWeather(lat, lon);
        if (cancelled) return;
        const next = { ...w, city: place?.city || FALLBACK.city, country: place?.country || FALLBACK.country };
        setState(next);
        writeCache(next);
      } catch {
        if (cancelled) return;
        const fallback = { temp: null, code: 0, city: FALLBACK.city, country: FALLBACK.country };
        setState((s) => s || fallback);
      }
    };

    if (!("geolocation" in navigator)) {
      run(FALLBACK.lat, FALLBACK.lon, { city: FALLBACK.city, country: FALLBACK.country });
      return () => { cancelled = true; };
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const place = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
        run(pos.coords.latitude, pos.coords.longitude, place);
      },
      () => run(FALLBACK.lat, FALLBACK.lon, { city: FALLBACK.city, country: FALLBACK.country }),
      { timeout: 5000, maximumAge: 30 * 60 * 1000 },
    );

    return () => { cancelled = true; };
  }, []);

  if (!state) return null;
  const { Icon, label } = describe(state.code);
  const place = state.country ? `${state.city}, ${state.country}` : state.city;
  return (
    <span className="inline-flex items-center gap-2" data-testid="header-weather">
      <Icon size={13} className="text-[var(--hinton-red)]" aria-hidden="true" />
      <span>{place}</span>
      {state.temp !== null && state.temp !== undefined && (
        <>
          <span aria-hidden="true">·</span>
          <span>{state.temp}°C</span>
          <span aria-hidden="true">·</span>
          <span>{label}</span>
        </>
      )}
    </span>
  );
}
